// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the two Claude sign-in steps (open the sign-in link, paste the code) as one
// inline block. The connect modal shows it in its sign-in screen with its own footer
// button; the Konvo panel shows the compact form with the Connect button inline.
// State lives in useClaudeLink so the host decides what happens once connected.

import styles from '@app/features/konvo_connections/components/KonvoClaudeSignIn.module.css';
import type {ClaudeLink} from '@app/features/konvo_connections/hooks/useClaudeLink';
import {Spinner} from '@app/features/ui/components/Spinner';
import {ArrowSquareOutIcon} from '@app/features/ui/icons/Icons';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';

const OPEN_DESCRIPTOR = msg({
	message: 'Open Claude',
	comment: 'Button in the inline Claude sign-in that opens the Claude sign-in page in a new tab.',
});
const CODE_PLACEHOLDER_DESCRIPTOR = msg({
	message: 'Paste code…',
	comment: 'Placeholder of the code field in the inline Claude sign-in.',
});
const CODE_LABEL_DESCRIPTOR = msg({
	message: 'Code from Claude',
	comment: 'Accessible label of the code field in the inline Claude sign-in.',
});
const CONNECT_DESCRIPTOR = msg({message: 'Connect', comment: 'Inline Claude sign-in button that checks the code.'});
const CHECKING_DESCRIPTOR = msg({
	message: 'Checking…',
	comment: 'Inline Claude sign-in button while the code is being checked.',
});
const START_AGAIN_DESCRIPTOR = msg({
	message: 'Get a new link',
	comment: 'Inline Claude sign-in button after the sign-in link expired or the code was refused.',
});

export function KonvoClaudeSignIn({
	claude,
	compact = false,
	onOpened,
	onSubmit,
}: {
	claude: ClaudeLink;
	/** The panel's form: short step titles and the Connect button next to the code field. */
	compact?: boolean;
	/** Called when the sign-in page is opened (tracking). */
	onOpened?: () => void;
	/** Compact only: runs the code check (the modal submits from its footer). */
	onSubmit?: () => void;
}) {
	const {i18n} = useLingui();
	const {link, starting, code, setCode, error, submitting, start, canSubmit} = claude;
	return (
		<div className={clsx(styles.steps, compact && styles.compact)} data-flx="konvo-claude-signin">
			<div className={styles.step}>
				<span className={styles.number} aria-hidden="true">
					1
				</span>
				<span className={styles.stepText}>
					{compact ? (
						<Trans>Sign in to Claude in a new tab</Trans>
					) : (
						<>
							<span className={styles.stepTitle}>
								<Trans>Sign in to Claude and approve</Trans>
							</span>
							<span className={styles.stepHint}>
								<Trans>Use the Claude account on your Nuvio subscription. The page ends with a code to copy.</Trans>
							</span>
						</>
					)}
				</span>
				{link ? (
					<button
						type="button"
						className={styles.open}
						onClick={() => {
							onOpened?.();
							void openExternalUrl(link.authUrl);
						}}
						data-flx="konvo-claude-signin.open"
					>
						<ArrowSquareOutIcon size={14} />
						{i18n._(OPEN_DESCRIPTOR)}
					</button>
				) : starting ? (
					<Spinner size="small" />
				) : (
					<button
						type="button"
						className={styles.again}
						onClick={() => void start()}
						data-flx="konvo-claude-signin.restart"
					>
						{i18n._(START_AGAIN_DESCRIPTOR)}
					</button>
				)}
			</div>
			<div className={clsx(styles.step, !code && styles.stepMuted)}>
				<span className={clsx(styles.number, styles.numberSoft)} aria-hidden="true">
					2
				</span>
				<span className={styles.stepText}>
					{compact ? (
						<Trans>Paste the code it shows you</Trans>
					) : (
						<span className={styles.stepTitle}>
							<Trans>Paste the code</Trans>
						</span>
					)}
				</span>
			</div>
			<div className={styles.codeRow}>
				<input
					className={styles.code}
					value={code}
					placeholder={i18n._(CODE_PLACEHOLDER_DESCRIPTOR)}
					aria-label={i18n._(CODE_LABEL_DESCRIPTOR)}
					autoComplete="off"
					spellCheck={false}
					disabled={!link || submitting}
					onChange={(event) => setCode(event.target.value)}
					onKeyDown={(event) => {
						if (event.key === 'Enter' && canSubmit) {
							event.preventDefault();
							onSubmit?.();
						}
					}}
					data-flx="konvo-claude-signin.code"
				/>
				{compact && (
					<button
						type="button"
						className={styles.connect}
						disabled={!canSubmit}
						onClick={() => onSubmit?.()}
						data-flx="konvo-claude-signin.connect"
					>
						{submitting ? <Spinner size="small" /> : null}
						{i18n._(submitting ? CHECKING_DESCRIPTOR : CONNECT_DESCRIPTOR)}
					</button>
				)}
			</div>
			{error && (
				<div className={styles.error} role="alert" data-flx="konvo-claude-signin.error">
					{error}
				</div>
			)}
		</div>
	);
}
